import { Field, Input } from '@apuestapr/ui';
import { useState, type ChangeEvent, type ReactElement } from 'react';

import type { ApiFailure } from '../api/client.ts';
import type { ImageUploader } from './upload.ts';

const uploadFailureMessage = (failure: ApiFailure): string => {
  if (failure.kind === 'http') {
    const detail = failure.details?.map(({ message }) => message).join(' ');
    return detail ?? failure.message;
  }
  if (failure.kind === 'malformed') return 'La respuesta de carga no coincide con el contrato.';
  return 'No se pudo conectar con el servicio de imágenes.';
};

export interface ImageFieldProps {
  readonly label: string;
  readonly controlId: string;
  readonly value: string;
  readonly nullable: boolean;
  readonly error?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly csrfToken: string;
  readonly uploader: ImageUploader;
  readonly onChange: (value: string) => void;
}

/** Manual/current URL plus the shared Input file chooser; failed uploads never replace the URL. */
export const ImageField = ({
  label,
  controlId,
  value,
  nullable,
  error,
  disabled = false,
  csrfToken,
  uploader,
  onChange,
}: ImageFieldProps): ReactElement => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const upload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (file === undefined) return;
    setUploading(true);
    setUploadError(undefined);
    const result = await uploader(file, csrfToken);
    setUploading(false);
    if (result.ok) onChange(result.data.url);
    else setUploadError(uploadFailureMessage(result.failure));
    // Permit choosing the same file again after a rejected upload.
    input.value = '';
  };

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-card p-4">
      <Field
        controlId={controlId}
        description={
          nullable
            ? 'Puede dejarse vacío. Se conserva una URL manual o existente si la carga no está configurada.'
            : 'Puede escribir una URL válida o cargar un archivo.'
        }
        error={error}
        label={`${label} · URL`}
        required={!nullable}
      >
        {(control) => (
          <Input
            {...control}
            disabled={disabled}
            type="url"
            value={value}
            onChange={(event) => onChange(event.currentTarget.value)}
          />
        )}
      </Field>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-foreground" htmlFor={`${controlId}-file`}>
          Cargar archivo para {label.toLocaleLowerCase('es-PR')}
        </label>
        <Input
          accept="image/jpeg,image/png,image/webp,image/gif"
          disabled={disabled || uploading}
          id={`${controlId}-file`}
          name="image"
          type="file"
          onChange={(event) => void upload(event)}
        />
        {uploading ? <p role="status">Cargando imagen…</p> : null}
        {uploadError === undefined ? null : (
          <div role="alert" className="grid gap-2 text-sm">
            <p className="m-0">{uploadError}</p>
            <p className="m-0 text-muted-foreground">
              La URL actual no cambió. Puede conservarla o escribir otra URL válida.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
